import { useState, useCallback } from 'react'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Input } from 'src/shared/components/ui/input'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { DeleteConfirmDialog } from '../../../../shared/DeleteConfirmDialog'
import { TableCell, TableRow } from 'src/shared/components/ui/table'
import { useQuoteSubItemApi } from 'src/api/db/apiQuoteSubItem'
import type { LucideIcon } from 'lucide-react'
import { Factory, Trash2 } from 'lucide-react'
import { useDebouncedFieldUpdate } from '../../../../../hooks'
import { LineItemNotesField } from '../../../../shared/LineItemNotesPopover'

export const MiscRow = ({
  id,
  handleDelete,
  description,
  unit_price,
  quantity,
  notes,
  isEditMode = false,
  showDiscountColumn = true,
  isReadOnly = false,
  icon: Icon = Factory,
  typeLabel,
}: {
  id: number
  handleDelete: () => void
  description: string
  unit_price: number
  quantity: number
  notes?: string | null
  isEditMode?: boolean
  showDiscountColumn?: boolean
  isReadOnly?: boolean
  icon?: LucideIcon
  typeLabel?: string
}) => {
  const { t } = useLocalization()
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const { updateQuoteSubItem } = useQuoteSubItemApi()

  const updateDescription = useCallback(
    (value: string) => updateQuoteSubItem.mutate({ id, description: value }),
    [id, updateQuoteSubItem]
  )
  const updateUnitPrice = useCallback(
    (value: number) => updateQuoteSubItem.mutate({ id, unit_price: value }),
    [id, updateQuoteSubItem]
  )
  const updateQuantity = useCallback(
    (value: number) => updateQuoteSubItem.mutate({ id, quantity: value }),
    [id, updateQuoteSubItem]
  )
  const updateNotes = useCallback(
    (value: string) =>
      updateQuoteSubItem.mutate({
        id,
        notes: value.trim() === '' ? null : value,
      }),
    [id, updateQuoteSubItem]
  )

  const [descriptionLocal, , handleDescriptionChange] = useDebouncedFieldUpdate(
    description,
    updateDescription
  )
  const [unitPriceLocal, , handleUnitPriceChange] = useDebouncedFieldUpdate(
    unit_price,
    updateUnitPrice
  )
  const [quantityLocal, , handleQuantityChange] = useDebouncedFieldUpdate(
    quantity,
    updateQuantity
  )
  const [notesLocal, , handleNotesChange] = useDebouncedFieldUpdate(
    notes ?? '',
    updateNotes
  )

  return (
    <>
      <TableRow className="border-b border-gray-200 hover:bg-gray-100/80 group">
        <TableCell className="py-2 px-5 pl-12 text-sm text-gray-700 align-middle border-l-2 border-amber-200">
          <Row className="gap-3 items-center">
            <div className="flex h-8 w-8 items-center justify-center rounded-sm border border-gray-200 p-1">
              <Icon className="ml-0.5 h-3.5 w-3.5 text-gray-700" />
            </div>

            <Column className="min-w-0 flex-1 gap-1">
              {typeLabel ? (
                <span className="inline-flex w-fit items-center rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700">
                  {typeLabel}
                </span>
              ) : null}
              {isEditMode ? (
                <Input
                  value={descriptionLocal}
                  onChange={(e) => handleDescriptionChange(e.target.value)}
                  className="h-7 min-w-0 border-gray-300 px-2 text-sm"
                />
              ) : (
                <p className="text-sm text-gray-900">{t(description)}</p>
              )}
              <LineItemNotesField
                value={notesLocal}
                onChange={handleNotesChange}
                canEdit={!isReadOnly}
                readOnlyNote={notes}
              />
            </Column>
          </Row>
        </TableCell>

        {showDiscountColumn && (
          <TableCell className="py-2 px-5 text-sm text-gray-700 align-middle text-center" />
        )}

        <TableCell className="py-2 px-5 text-sm text-gray-700 align-middle text-right">
          <Row className="items-center justify-end gap-3">
            {isEditMode ? (
              <input
                type="number"
                value={quantityLocal}
                onChange={(e) =>
                  handleQuantityChange(Number(e.target.value) || 1)
                }
                className="input-style h-7 w-14 shrink-0 px-1 text-center text-sm tabular-nums"
              />
            ) : (
              <span className="w-14 shrink-0 text-center tabular-nums">
                {quantity}
              </span>
            )}
            {isEditMode ? (
              <Input
                type="number"
                value={unitPriceLocal}
                onChange={(e) =>
                  handleUnitPriceChange(Number(e.target.value) || 0)
                }
                className="h-7 w-20 px-1 text-right text-sm"
              />
            ) : (
              <span>{unit_price * quantity}</span>
            )}
            <button
              onClick={() => setShowDeleteDialog(true)}
              className="rounded p-1 opacity-0 transition-opacity hover:bg-red-50 group-hover:opacity-100"
            >
              <Trash2 className="h-3.5 w-3.5 text-gray-400 hover:text-red-500" />
            </button>
          </Row>
        </TableCell>
      </TableRow>

      <DeleteConfirmDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        title={t('Delete row', 'Ta bort rad')}
        description={`${t(
          'Are you sure you want to delete this row?',
          'Är du säker på att du vill ta bort denna rad?'
        )} ${t(
          'This action cannot be undone.',
          'Denna åtgärd kan inte ångras.'
        )}`}
        onConfirm={handleDelete}
      />
    </>
  )
}
